import {
  TableCellProps,
  WbCardContent,
  WbTable,
  WbWidget,
  customAlertApiRef,
} from '@agilelab/plugin-wb-platform';
import { HelpIcon } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  Box,
  Chip,
  LinearProgress,
  LinearProgressProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import CheckCircle from '@material-ui/icons/CheckCircle';
import RadioButtonUnchecked from '@material-ui/icons/RadioButtonUnchecked';
import { Alert } from '@material-ui/lab';
import { FC, useEffect } from 'react';
import { useMigrationContext } from '../../hooks/migration/useMigrationContext';
import { MigrationStep } from '../../types';

const LinearProgressWithLabel = (
  props: LinearProgressProps & { value: number },
) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography color="textSecondary">{`${Math.round(
          props.value,
        )}%`}</Typography>
      </Box>
    </Box>
  );
};

const migrationStatusColumns = (theme: Theme) =>
  [
    {
      field: 'isComplete',
      headerName: 'Status',
      width: 120,
      fieldRender: e =>
        e.isComplete ? (
          <Tooltip title="Completed">
            <Chip
              label="Completed"
              variant="outlined"
              icon={
                <CheckCircle style={{ color: theme.palette.success.main }} />
              }
            />
          </Tooltip>
        ) : (
          <Tooltip title="To do">
            <RadioButtonUnchecked color="disabled" />
          </Tooltip>
        ),
    },
    {
      field: 'description',
      headerName: 'Description',
      fieldRender: e => {
        const desc = (
          <Typography
            style={e.isComplete ? {} : { color: theme.palette.text.disabled }}
          >
            {e.description}
          </Typography>
        );
        return e.additionalInfo ? (
          <Tooltip title={e.additionalInfo}>{desc}</Tooltip>
        ) : (
          desc
        );
      },
    },
  ] as TableCellProps<MigrationStep>[];

export const MigrationStepsWidget: FC<{}> = () => {
  const theme = useTheme();
  const { fetchMigrationStepsState } = useMigrationContext();
  const migrationSteps = fetchMigrationStepsState.value ?? [];
  const alertApi = useApi(customAlertApiRef);

  useEffect(() => {
    if (fetchMigrationStepsState.error) {
      alertApi.post({
        message: `Error while fetching migration steps: ${fetchMigrationStepsState.error.message}`,
        severity: 'error',
      });
    }
  }, [fetchMigrationStepsState, alertApi]);

  return (
    <WbWidget
      title="Migration steps"
      footer={
        !fetchMigrationStepsState.loading && migrationSteps.length ? (
          <Box sx={{ width: '100%' }} padding={1}>
            <LinearProgressWithLabel
              value={
                (migrationSteps.filter(v => v.isComplete).length /
                  migrationSteps.length) *
                100
              }
            />
          </Box>
        ) : (
          <></>
        )
      }
    >
      <WbCardContent>
        <Alert
          severity="info"
          variant="outlined"
          icon={<HelpIcon />}
          style={{ border: 'none' }}
        >
          <Typography>
            Complete the following steps in order. The panels on this page will
            guide you through the process
          </Typography>
        </Alert>
        <WbTable<MigrationStep>
          components={{
            tableLoader: { loading: fetchMigrationStepsState.loading },
            tableContent: {
              columns: migrationStatusColumns(theme),
              rows: migrationSteps,
            },
          }}
        />
      </WbCardContent>
    </WbWidget>
  );
};
